<template>
    <div class="container mx-auto max-w-xl flex-1 px-4 md:px-0 mb-8">
        <div class="mb-10">
            <DField
                label="Select student/s"
                required
                required-color="text-red-600"
                class="mb-4"
            />
            <!-- Use a unique id for each option, so that DRadio will work for our needs (we don't want the default behaviour) -->
            <DField
                v-for="student in students"
                :id="`student-${student.external_id}`"
                :key="student.external_id"
            >
                <div class="flex w-full items-center">
                    <div
                        v-if="studentDeletionUrl"
                        class="flex-grow-0 mr-2 lg:-ml-8"
                    >
                        <DButton
                            theme="text-link"
                            link-color="base"
                            class="text-action-destructive"
                            size="sm"
                            @click="deleteStudent(student)"
                        >
                            <DIcon
                                icon="heroicons:x-circle-20-solid"
                                size="w-6 h-6"
                                class="flex!"
                            />
                        </DButton>
                    </div>
                    <div class="flex-1">
                        <DCheckbox
                            v-if="canSelectMultiple"
                            :model-value="selectedStudents[student.external_id]"
                            card
                            checkbox-position="right"
                            @update:model-value="toggleStudent(student)"
                        >
                            <template #icon>
                                <UIAvatar
                                    :avatar="student.avatar"
                                    size-override="medium-small"
                                />
                            </template>
                            {{ student.avatar.description }}
                        </DCheckbox>
                        <DRadio
                            v-else
                            :model-value="selectedStudents[student.external_id]"
                            :native-value="true"
                            card
                            radio-position="right"
                            @update:model-value="toggleStudent(student)"
                        >
                            <template
                                v-if="student.avatar"
                                #icon
                            >
                                <UIAvatar
                                    :avatar="student.avatar"
                                    size-override="medium-small"
                                />
                            </template>
                            {{ student.avatar.description }}
                        </DRadio>
                    </div>
                </div>
            </DField>
        </div>
        <DButton
            wide
            class="rounded-lg"
            :disabled="!selectedStudentIds.length"
            @click="onClick"
        >
            Next
        </DButton>
    </div>
</template>

<script setup>
    import { useRouter } from 'vue-router'
    import {
        DField, DCheckbox, DButton, DRadio, DIcon,
    } from '@digistorm/spark'
    import UIAvatar from '@/components/ui/UIAvatar.vue'
    import api from '@/services/api'
    import funnelFormStudentSelectionType from '@/constants/funnelFormStudentSelectionType'
    import { useLoadingOverlays } from '@/composables/loadingOverlays'

    const emit = defineEmits(['entry'])

    const props = defineProps({
        config: Object,
    })

    const router = useRouter()
    const moduleHelpers = useModuleHelpers()
    const loadingOverlays = useLoadingOverlays()

    const selectedStudents = ref({})
    const clicking = ref(false)

    const content = computed(() => props.config.data)
    const students = computed(() => content.value.students)
    const studentDeletionUrl = computed(() => content.value.student_deletion_url)
    const canSelectMultiple = computed(() => content.value.student_selection_type === funnelFormStudentSelectionType.multiple)

    const selectedStudentIds = computed(() => {
        return transform(selectedStudents.value, (selected, studentIsSelected, studentId) => {
            if (studentIsSelected) {
                selected.push(studentId)
            }
        }, [])
    })

    const deselectOtherStudents = (studentId) => {
        each(selectedStudents.value, (student, id) => {
            if (id !== studentId) {
                selectedStudents.value[id] = false
            }
        })
    }

    const toggleStudent = (student) => {
        if (!canSelectMultiple.value) {
            deselectOtherStudents(student.external_id)
        }
        selectedStudents.value[student.external_id] = !selectedStudents.value[student.external_id]
    }

    const deleteStudent = (student) => {
        loadingOverlays.push()

        api.post(studentDeletionUrl.value, {
            student: student.external_id,
        })
            .then(({ data }) => {
                return moduleHelpers.handleActions(router, data.actions)
            })
            .finally(() => {
                loadingOverlays.pop()
            })
    }

    const onClick = () => {
        if (clicking.value) {
            return
        }

        const link = {
            ...props.config.link,
            params: {
                students: selectedStudentIds.value,
            },
        }

        clicking.value = true
        moduleHelpers.openModuleOrOpenBrowserForLinkData(
            router,
            link,
        )
            .catch((e) => {
                console.log('Failed to open link', e)
            })
            .finally(() => {
                clicking.value = false
            })
    }
</script>
